import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Redirect, useParams } from 'react-router-dom';

// mui
import {
  Box,
  Card as MuiCard,
  CircularProgress,
  Divider,
  Grid as MuiGrid,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

// components
import withRouter from '../../../components/hocs/withRouter';
import { PubSub } from '../../../utils/eventUtils';

import SocialEnggPH from '../../../components/main/settings/orgProfile/SocialEnggPH';
import AttestSubmit from './AttestSubmit';
import P250SecurityQuestions from '../../../components/main/settings/orgProfile/P250SecurityQuestions';
import P100SecurityQuestions from '../../../components/main/settings/orgProfile/P100SecurityQuestions';
import { OrganizationContact } from '../../../public/quote/confirm/OrganizationContact';
import { OtherQuestions } from '../../../components/main/settings/orgProfile/OtherQuestions';
import { QuoteStepLoader } from './QuoteStepLoader';
import { AccountDetails } from '../../../public/quote/confirm/AccountDetails';
import SecurityQuestionsView from '../../_tabs/securityQuestions/SecurityQuestionsView';
import { BoostRaterVersionResponse } from '../../workflows/quotes/prime-100/api/types';
import { resolveP100RaterVersionFromEntity } from '../../workflows/quotes/prime-100/utils';

// services
import {
  fetchQuoteDetails,
  fetchP250QuotePageDetails,
} from '../../../inbox/QuotesService';
import { QuoteStepsP250, QuoteStepsP100 } from './QuoteSteps';
import { ProductTypes } from '../../../types';
import { REDACTED_FIELD } from '../../../config/logrocket';
import useNotifications from '../../../_lib/notifications/useNotifications';

// this file is only for attestation of normal quote flow (no renewals,endorsements)

export const AttestQuote = withRouter(({ history, location }: any) => {
  const notifications = useNotifications();
  const classes = useStyles();
  // @ts-expect-error TS(2339): Property 'quoteId' does not exist on type '{}'.
  const { quoteId, product } = useParams();

  const isP100Product = product === ProductTypes.p100;
  const isP250Product = product === ProductTypes.p250;

  const [quote, setQuote] = useState({});
  const [loading, setLoading] = useState(true);
  const [invalidQuote, setInvalid] = useState(false);

  const route = location.pathname.split('/')[1];

  const {
    // @ts-expect-error TS(2339): Property 'firmographicData' does not exist on type... Remove this comment to see the full error message
    firmographicData,
    // @ts-expect-error TS(2339): Property 'initialRequestData' does not exist on ty... Remove this comment to see the full error message
    initialRequestData,
    // @ts-expect-error TS(2339): Property 'accountId' does not exist on type '{}'.
    accountId,
    // @ts-expect-error TS(2339): Property 'companyName' does not exist on type '{}'... Remove this comment to see the full error message
    companyName,
    // @ts-expect-error TS(2339): Property 'customerStatus' does not exist on type '... Remove this comment to see the full error message
    customerStatus,
  } = quote;

  const boostRaterVersion = resolveP100RaterVersionFromEntity(quote);

  const renderP100V6SecurityQuestions =
    isP100Product &&
    Boolean(quoteId) &&
    boostRaterVersion === BoostRaterVersionResponse.V6;

  const callQuoteDetails = () => {
    if (isP100Product) {
      return fetchQuoteDetails(quoteId).then((response) => {
        if (
          response.data.customerStatus === 'INVALID' ||
          response.data.customerStatus === 'REFERRED'
        ) {
          setInvalid(true);
        }

        setQuote(response.data);
      });
    }

    if (isP250Product) {
      return fetchP250QuotePageDetails(quoteId)
        .then((response) => {
          if (
            response.data.customerStatus === 'INVALID' ||
            response.data.customerStatus === 'REFERRED'
          ) {
            setInvalid(true);
          }
          setQuote(response.data);
        })
        .catch(console.error.bind(console));
    }

    return Promise.resolve();
  };

  useEffect(() => {
    const invQ = PubSub.subscribe('quote-invalidated', (value: any) => {
      setInvalid(value);
    });

    const refQ = PubSub.subscribe('fetch-details', () => {
      callQuoteDetails();
    });

    return () => {
      invQ.remove();
      refQ.remove();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setLoading(true);
    callQuoteDetails()
      .catch(console.error.bind(console))
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return <QuoteStepLoader />;
  }

  if (customerStatus === 'SIGNED') {
    return <Redirect to="/customer/policies" />;
  }

  if (_.isEmpty(quote)) {
    return null;
  }

  if (invalidQuote) {
    setTimeout(() => {
      history.push('/customer/policies');
      notifications.success(
        'Quote changes must go through Underwriter review, please wait!'
      );
    }, 2000);
  }

  return (
    <>
      {isP100Product && (
        <QuoteStepsP100
          location={location}
          agencyBilled={firmographicData.agencyBilled}
        />
      )}
      {isP250Product && (
        <QuoteStepsP250
          location={location}
          agencyBilled={firmographicData.agencyBilled}
        />
      )}

      <section>
        <MuiCard>
          <Box pl={3} pr={3} pb={3}>
            <h1 className={`${classes.headings} ${REDACTED_FIELD}`}>
              {companyName}
            </h1>

            <MuiGrid container>
              <MuiGrid item md={10}>
                <Divider className={classes.divider} />
                <AccountDetails
                  parentClasses={classes.card}
                  account={firmographicData}
                  accountId={accountId}
                  quote={quote}
                  from="attestation"
                  route={route}
                  productType={product}
                />
              </MuiGrid>
              <MuiGrid item md={10}>
                <Divider className={classes.divider} />
                <OrganizationContact
                  parentClasses={classes.card}
                  data={quote}
                  accountId={accountId}
                  readOnly
                  route={route}
                  from="attestation"
                />
              </MuiGrid>
              <MuiGrid item md={10}>
                <Divider className={classes.divider} />
                {isP100Product &&
                  renderP100V6SecurityQuestions && (
                    <Box className={classes.card}>
                      <SecurityQuestionsView
                        quoteId={quoteId}
                        productType={ProductTypes.p100}
                        isEditable={false}
                        withDefaults
                      />
                    </Box>
                  )}
                {isP100Product &&
                  !renderP100V6SecurityQuestions && (
                    <P100SecurityQuestions
                      parentClasses={classes.card}
                      inData={firmographicData}
                      from="attestation"
                      accountId={accountId}
                    />
                  )}
                {isP250Product && (
                  <P250SecurityQuestions
                    parentClasses={classes.card}
                    data={firmographicData}
                    from="attestation"
                    accountId={accountId}
                  />
                )}
              </MuiGrid>

              <MuiGrid item md={10}>
                {isP250Product && (
                  <>
                    <Divider className={classes.divider} />
                    <OtherQuestions
                      parentClasses={classes.card}
                      accountId={accountId}
                      data={quote}
                      from="attestation"
                      quote={quote}
                      route={route}
                    />
                  </>
                )}
                {isP100Product &&
                  !firmographicData.excludedFromSE &&
                  initialRequestData.socialEngEndorsement && (
                    <>
                      <Divider className={classes.divider} />
                      <SocialEnggPH
                        parentClasses={classes.card}
                        accountId={accountId}
                        orgData={firmographicData}
                        from="attestation"
                      />
                    </>
                  )}
              </MuiGrid>
              <AttestSubmit quote={quote} />
            </MuiGrid>
          </Box>
        </MuiCard>
      </section>
    </>
  );
});

const useStyles = makeStyles(({ config, palette }) => ({
  divider: {
    margin: '2rem 0',
  },
  headings: {
    margin: 0,
    marginTop: '2rem',
    fontSize: config.textSizes.canon,
    lineHeight: 1,
    textAlign: 'left',
    color: `${palette.primary.contrastText} !important`,
    fontWeight: config.weights.bold,
  },
  card: {
    padding: '1.6666666667rem',
    border: '1px solid #707070',
    backgroundColor: 'rgba(38, 38, 46, .5)',
    color: 'white',
  },
}));
